import classNames from 'classnames';

import {
  isSimulatedFixture,
  type GameweekMatchSummary
} from '@api-service/live-matches';

type FixtureListProps = {
  fixtures: GameweekMatchSummary[];
  selectedMatchIds: string[];
  onToggle: (matchId: string) => void;
};

/**
 * The gameweek's fixtures, simulated ones marked.
 *
 * A simulated fixture copies the real one's gameweek, so the list carries both
 * and the pair reads as a duplicate unless the marking is obvious. Simulated
 * ones are listed first for the same reason: this picker exists to reach them,
 * and hunting for `sim-` in a list of twenty is not a good use of anyone's
 * time.
 */
export const FixtureList = ({
  fixtures,
  selectedMatchIds,
  onToggle
}: FixtureListProps) => {
  const ordered = [...fixtures].sort((a, b) => {
    const simulatedDelta =
      Number(isSimulatedFixture(b)) - Number(isSimulatedFixture(a));
    if (simulatedDelta !== 0) return simulatedDelta;
    return a.kickoffTime.localeCompare(b.kickoffTime);
  });

  if (ordered.length === 0) {
    return (
      <p className="text-sm text-neutral-600" data-cy="harness-no-fixtures">
        No fixtures for this gameweek.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1" data-cy="harness-fixture-list">
      {ordered.map((fixture) => {
        const isSelected = selectedMatchIds.includes(fixture.matchId);
        const isSimulated = isSimulatedFixture(fixture);

        return (
          <li key={fixture.matchId}>
            <label
              className={classNames(
                'flex cursor-pointer items-center gap-2 rounded border px-2 py-1.5 text-sm',
                isSelected
                  ? 'border-primary-600 bg-primary-50'
                  : 'border-neutral-300 bg-white'
              )}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onToggle(fixture.matchId)}
                data-cy="harness-fixture-toggle"
                data-cy-match-id={fixture.matchId}
              />
              <span className="flex-1 truncate">
                {fixture.homeTeam.name} v {fixture.awayTeam.name}
              </span>
              {isSimulated && (
                <span className="rounded bg-secondary-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  {fixture.provenance}
                </span>
              )}
              <span className="w-24 shrink-0 text-right text-xs text-neutral-600">
                {fixture.status}
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
};
